import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/solid';
import { useEffect, useRef, useState } from 'react';
import getCity from '../../../api/getCity';

function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}

const FormComboBox = ({
    label,
    data,
    value,
    onChange,
    className,
    multiple,
    disabled,
    fetchCitiesOnOpen = false,
    placeholder = 'Seleccione...'
}) => {
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState(data || []);
    const [isLoading, setIsLoading] = useState(false);
    const [didFetchCities, setDidFetchCities] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const buttonRef = useRef(null);
    const isFetchingRef = useRef(false);
    const [isDropdownForcedOpen, setIsDropdownForcedOpen] = useState(false);

    useEffect(() => {
        setOptions(data || []);
    }, [data]);

    const fetchCities = async () => {
        if (!fetchCitiesOnOpen || didFetchCities || (options?.length || 0) > 0 || isFetchingRef.current) return;

        isFetchingRef.current = true;
        setIsLoading(true);
        setErrorMessage('');
        try {
            const cities = await getCity(setErrorMessage);
            if (cities?.length > 0) {
                const formattedCities = cities
                    .map((city) => ({ ...city, label: city?.name, value: city?.name }))
                    .sort((a, b) => {
                        const labelA = (a.label || '').toLowerCase().trim();
                        const labelB = (b.label || '').toLowerCase().trim();
                        return labelA.localeCompare(labelB, 'es', {
                            sensitivity: 'base',
                            numeric: true,
                            ignorePunctuation: true
                        });
                    });
                setOptions(formattedCities);
            } else {
                setOptions([]);
            }
            setDidFetchCities(true);
        } finally {
            setIsLoading(false);
            isFetchingRef.current = false;
        }
    };

    const filteredData =
        query === ''
            ? options
            : options.filter((element) => {
                  return element.label.toLowerCase().includes(query.toLowerCase());
              });

    const handleOpenDropdown = () => {
        setIsDropdownForcedOpen(true);
        fetchCities();
    };

    const handleSelect = (selectedValue) => {
        setIsDropdownForcedOpen(false);
        onChange(selectedValue);
    };

    return (
        <Combobox
            as="div"
            value={value}
            onChange={handleSelect}
            className={className}
            multiple={multiple}
            disabled={disabled}
        >
            {({ open }) => (
                <>
                    <Combobox.Label className="block text-sm font-medium text-gray-700">{label}</Combobox.Label>
                    <div
                        className="relative"
                    >
                        <Combobox.Input
                            className="block w-full rounded-full border border-gray-300 bg-white py-2 pl-3 pr-10 shadow-sm focus:border-dl-primary-500 focus:outline-none focus:ring-1 focus:ring-dl-primary-500 sm:text-sm"
                            onChange={(event) => setQuery(event.target.value)}
                            onBlur={() => {
                                // Delay allows option click to register before closing.
                                setTimeout(() => setIsDropdownForcedOpen(false), 120);
                            }}
                            placeholder={placeholder}
                            displayValue={(element) => element?.value}
                        />
                        <Combobox.Button
                            ref={buttonRef}
                            onClick={handleOpenDropdown}
                            className="absolute inset-y-0 right-0 z-10 flex items-center rounded-r-md px-3 focus:outline-none"
                        >
                            <ChevronDownIcon className="combobox-arrow-icon h-5 w-5 text-gray-500" aria-hidden="true" />
                        </Combobox.Button>

                        {(open || isDropdownForcedOpen) && (
                            <Combobox.Options className="absolute z-10 mt-1 max-h-60 min-w-[115%] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {isLoading ? (
                                    <div className="px-3 py-2 text-sm text-gray-500">Loading cities...</div>
                                ) : filteredData.length > 0 ? (
                                    filteredData.map((element) => (
                                        <Combobox.Option
                                            disabled={element.disabled}
                                            key={element.id}
                                            value={element}
                                            className={({ active }) =>
                                                classNames(
                                                    'relative cursor-default select-none py-2 pl-3 pr-9',
                                                    active ? 'bg-dl-secondary text-white' : 'text-gray-900'
                                                )
                                            }
                                        >
                                            {({ active, selected }) => (    
                                                <>
                                                    <span className={classNames('block truncate', selected && 'font-semibold')}>
                                                        {element.label}
                                                    </span>

                                                    {selected && (
                                                        <span
                                                            className={classNames(
                                                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                                                active ? 'text-white' : 'text-dl-accent'
                                                            )
                                                        }
                                                        >
                                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </Combobox.Option>
                                    ))
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                        {errorMessage || 'No cities found'}
                                    </div>
                                )}
                            </Combobox.Options>
                        )}
                    </div>
                </>
            )}
        </Combobox>
    );
};

export default FormComboBox;
